import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LandingFooter from '../../components/layouts/LandingFooter';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { t } from '../../utils/translations';
import { handleHashScroll } from '../../utils/scrollToSection';
import { useSubmitContactMutation } from '../../store/api/landingApi';
import { showSuccess, showError } from '../../utils/toast';

import homeTree from '../../assets/images/home-tree.png';
import vision from '../../assets/images/vision.png';
import message from '../../assets/images/message.png';
import objectives from '../../assets/images/objectives.png';
import values from '../../assets/images/values.png';
import mobileImg from '../../assets/images/mobile-img.png';

import drFesilah from '../../assets/images/drfesilah.png';
import drFesilahSelected from '../../assets/images/drfesilah_selected.png';
import scientific from '../../assets/images/scientific.png';
import scientificSelected from '../../assets/images/scientific_selected.png';
import agricultural from '../../assets/images/agricultural.png';
import agriculturalSelected from '../../assets/images/agricultural_selected.png';
import agriculturalActivities from '../../assets/images/agricultural_activities.png';
import agriculturalActivitiesSelected from '../../assets/images/agricultural_activities_selected.png';
import outdoor from '../../assets/images/outdoor.png';
import outdoorSelected from '../../assets/images/outdoor_selected.png';
import indoor from '../../assets/images/indoor.png';
import indoorSelected from '../../assets/images/indoor_selected.png';
import google from '../../assets/images/google.png';
import apple from '../../assets/images/apple.png';
import bg from '../../assets/images/bg.png';

const Home: React.FC = () => {
  const [lang, setLang] = useState('en');
  const [activeTab, setActiveTab] = useState('v-pills-profile');
  const [openAccordion, setOpenAccordion] = useState<string | null>('collapseOne');
  const [submitContact, { isLoading: isSubmittingContact }] = useSubmitContactMutation();
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    message: '',
  });

  useEffect(() => {
    const savedLang = localStorage.getItem('locale') || 'en';
    setLang(savedLang);
    
    // Apply RTL to body when Arabic is selected
    if (savedLang === 'ar') {
      document.body.setAttribute('dir', 'rtl');
      document.body.classList.add('rtl');
    } else {
      document.body.setAttribute('dir', 'ltr');
      document.body.classList.remove('rtl');
    }
    
    AOS.init({
      duration: 1000,
      delay: 300,
    });

    // Handle hash scroll on page load
    handleHashScroll(100);
  }, []);

  const imgAr = lang === 'ar' ? '-ar' : '';

  return (
    <>
      <div className="d-flex flex-column bg-base-gradient flex-center w-100 min-h-300px min-h-lg-500px px-9">
        <div className="container">
          <div className="row mb-18">
            <div className="col-md-6 text-left">
              <div className="mb-5 mb-lg-10 py-10 py-lg-20">
                <h1 className="text-base lh-base fw-bold display-3 mb-5 mt-15" data-aos="fade-up" dangerouslySetInnerHTML={{ __html: t('Home_Title', lang) }}></h1>
                <h6 className="fw-bold mt-2 fs-1" data-aos="fade-up">
                  {t('home_desc', lang)}
                </h6>
                <br />
              </div>
            </div>
            <div className="col-md-6 text-right  position-relative" data-aos="fade-left">
              <img
                src={homeTree}
                style={{
                  position: 'absolute',
                  left: lang === 'ar' ? '-20.5vw' : 'auto',
                  right: lang === 'ar' ? 'auto' : '-6.5vw',
                  transform: lang === 'ar' ? 'scaleX(-1)' : 'none',
                }}
                className={`ps-20 w-lg-100 w-75 mt-n20 mt-lg-0 image${imgAr}`}
                alt="tree"
              />
            </div>
          </div>
        </div>
      </div>

      <section className="about min-h-200px" id="about">
        <div className="container" data-aos="flip-up">
          <h1 className="text-base lh-base fw-bold fs-2x fs-lg-3x mb-5 mt-15" dangerouslySetInnerHTML={{ __html: t('about_title', lang) }}></h1>
          <p className="fs-4 text-gray-600">
            {t('about_desc', lang)}
          </p>
        </div>
      </section>

      <section className="vision min-h-300px">
        <div className="container" data-aos="fade-up">
          <div className="row">
            <div className="col-md-6 pe-20">
              <img src={vision} className={`w-100 image${imgAr}`} alt="vision" />
            </div>
            <div className="col-md-6">
              <h1 className="lh-base fw-bold fs-2x fs-lg-3x mb-5">{t('Vision', lang)}</h1>
              <p className="fs-4 text-gray-600">
                {t('vision_desc', lang)}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="message mt-20">
        <div className="container" data-aos="flip-left">
          <div className="row">
            <div className="col-md-6">
              <h1 className="lh-base fw-bold fs-2x fs-lg-3x mb-5">{t('Message', lang)}</h1>
              <p className="fs-4 text-gray-600 pe-5">
                {t('message_desc', lang)}
              </p>
            </div>
            <div className="col-md-6 px-20">
              <img src={message} className={`w-100 image${imgAr} d-none d-md-block`} alt="message" />
            </div>
          </div>
        </div>
      </section>

      <section className="objectives mt-20">
        <div className="container" data-aos="flip-right">
          <div className="row">
            <div className="col-md-6 pe-20">
              <img src={objectives} className={`w-100 image${imgAr}`} alt="objectives" />
            </div>
            <div className="col-md-6">
              <h1 className="lh-base fw-bold fs-2x fs-lg-3x mb-5">{t('Objectives', lang)}</h1>
              <ul className="font-list">
                <li className="fs-4 text-gray-600 my-10">
                  <i className={`fas fa-caret-${lang === 'ar' ? 'left fs-2' : 'right'} green-arrow`}></i>
                  <span className={lang === 'ar' ? 'me-5' : 'ms-5'}>{t('objective1', lang)}</span>
                </li>
                <li className="fs-4 text-gray-600 my-10">
                  <i className={`fas fa-caret-${lang === 'ar' ? 'left fs-2' : 'right'} green-arrow`}></i>
                  <span className={lang === 'ar' ? 'me-5' : 'ms-5'}>{t('objective2', lang)}</span>
                </li>
                <li className="fs-4 text-gray-600 my-10">
                  <i className={`fas fa-caret-${lang === 'ar' ? 'left fs-2' : 'right'} green-arrow`}></i>
                  <span className={lang === 'ar' ? 'me-5' : 'ms-5'}>{t('objective3', lang)}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="values mt-20">
        <div className="container" data-aos="flip-left">
          <div className="row">
            <div className="col-6">
              <h1 className="lh-base fw-bold fs-2x fs-lg-3x mb-5">{t('Values', lang)}</h1>
              <ul className="font-list">
                <li className="fs-4 text-gray-600 my-10">
                  <i className={`fas fa-caret-${lang === 'ar' ? 'left fs-2' : 'right'} green-arrow`}></i>
                  <span className={lang === 'ar' ? 'me-5' : 'ms-5'}>{t('value1', lang)}</span>
                </li>
                <li className="fs-4 text-gray-600 my-10">
                  <i className={`fas fa-caret-${lang === 'ar' ? 'left fs-2' : 'right'} green-arrow`}></i>
                  <span className={lang === 'ar' ? 'me-5' : 'ms-5'}>{t('value2', lang)}</span>
                </li>
                <li className="fs-4 text-gray-600 my-10">
                  <i className={`fas fa-caret-${lang === 'ar' ? 'left fs-2' : 'right'} green-arrow`}></i>
                  <span className={lang === 'ar' ? 'me-5' : 'ms-5'}>{t('value3', lang)}</span>
                </li>
                <li className="fs-4 text-gray-600 my-10">
                  <i className={`fas fa-caret-${lang === 'ar' ? 'left fs-2' : 'right'} green-arrow`}></i>
                  <span className={lang === 'ar' ? 'me-5' : 'ms-5'}>{t('value4', lang)}</span>
                </li>
                <li className="fs-4 text-gray-600 my-10">
                  <i className={`fas fa-caret-${lang === 'ar' ? 'left fs-2' : 'right'} green-arrow`}></i>
                  <span className={lang === 'ar' ? 'me-5' : 'ms-5'}>{t('value5', lang)}</span>
                </li>
              </ul>
            </div>
            <div className="col-6 ps-lg-20">
              <img src={values} className={`w-100 image${imgAr}`} alt="values" />
            </div>
          </div>
        </div>
      </section>

      <section className="features mt-20" id="features">
        <div className="container mt-lg-20" data-aos="fade-up">
          <br />
          <h1 className="text-base lh-base fw-bold fs-2x fs-lg-3x mb-5 mt-20" dangerouslySetInnerHTML={{ __html: t('feature_title', lang) }}></h1>
          <div className="row align-items-start">
            <div className="nav col-md-3 nav-pills me-3" id="v-pills-tab" role="tablist" aria-orientation="vertical">
              <button 
                className={`nav-link ctab-link w-100 ${lang === 'ar' ? 'text-end' : ''} ${activeTab === 'v-pills-profile' ? 'active' : ''}`}
                onClick={() => setActiveTab('v-pills-profile')}
                type="button"
              >
                <img src={activeTab === 'v-pills-profile' ? drFesilahSelected : drFesilah} className="w-30px me-5" alt="icon" /> {t('Dr. Fesilah', lang)}
              </button>
              <button 
                className={`nav-link ctab-link w-100 ${lang === 'ar' ? 'text-end' : ''} ${activeTab === 'v-pills-messages' ? 'active' : ''}`}
                onClick={() => setActiveTab('v-pills-messages')}
                type="button"
              >
                <img src={activeTab === 'v-pills-messages' ? scientificSelected : scientific} className="w-30px me-5" alt="icon" /> {t('Scientific Articles', lang)}
              </button>
              <button 
                className={`nav-link ctab-link w-100 ${lang === 'ar' ? 'text-end' : ''} ${activeTab === 'v-pills-fairs' ? 'active' : ''}`}
                onClick={() => setActiveTab('v-pills-fairs')}
                type="button"
              >
                <img src={activeTab === 'v-pills-fairs' ? agriculturalSelected : agricultural} className="w-30px me-5" alt="icon" /> {t('Agricultural Fairs', lang)}
              </button>
              <button 
                className={`nav-link ctab-link w-100 ${lang === 'ar' ? 'text-end' : ''} ${activeTab === 'v-pills-settings' ? 'active' : ''}`}
                onClick={() => setActiveTab('v-pills-settings')}
                type="button"
              >
                <img src={activeTab === 'v-pills-settings' ? agriculturalActivitiesSelected : agriculturalActivities} className="w-30px me-5" alt="icon" /> {t('Agricultural Activities', lang)}
              </button>
              <button 
                className={`nav-link ctab-link w-100 ${lang === 'ar' ? 'text-end' : ''} ${activeTab === 'v-pills-outdoor' ? 'active' : ''}`}
                onClick={() => setActiveTab('v-pills-outdoor')}
                type="button"
              >
                <img src={activeTab === 'v-pills-outdoor' ? outdoorSelected : outdoor} className="w-30px me-5" alt="icon" /> {t('Outdoor Plants', lang)}
              </button>
              <button 
                className={`nav-link ctab-link w-100 ${lang === 'ar' ? 'text-end' : ''} ${activeTab === 'v-pills-indoor' ? 'active' : ''}`}
                onClick={() => setActiveTab('v-pills-indoor')}
                type="button"
              >
                <img src={activeTab === 'v-pills-indoor' ? indoorSelected : indoor} className="w-30px me-5" alt="icon" /> {t('Indoor Plants', lang)}
              </button>
            </div>
            <div className="tab-content col-md-8" id="v-pills-tabContent">
              <div className={`tab-pane fade ${activeTab === 'v-pills-profile' ? 'show active' : ''}`}>
                <div className="row">
                  <div className="col-1">
                    <div className="vertical-progress">
                      <div className="progress-bar" style={{ height: '30%' }}></div>
                    </div>
                  </div>
                  <div className="col-5 d-none d-md-block">
                    <img src={mobileImg} className="w-100 mobile-img p-5" style={{ marginTop: '-12rem' }} alt="mobile" />
                  </div>
                  <div className="col-md-6 col-10">
                    <h1 className="text-base lh-base fw-bold fs-1x fs-lg-2x mb-5">{t('Dr. Fesilah', lang)}</h1>
                    <p className="fs-4 text-gray-600 lh-lg">
                      {t('dr_fesilah_desc', lang)}
                    </p>
                  </div>
                </div>
              </div>
              <div className={`tab-pane fade ${activeTab === 'v-pills-messages' ? 'show active' : ''}`}>
                <div className="row">
                  <div className="col-1">
                    <div className="vertical-progress">
                      <div className="progress-bar" style={{ height: '50%' }}></div>
                    </div>
                  </div>
                  <div className="col-5 d-none d-md-block">
                    <img src={mobileImg} className="w-100 mobile-img p-5" style={{ marginTop: '-12rem' }} alt="mobile" />
                  </div>
                  <div className="col-md-6 col-10">
                    <h1 className="text-base lh-base fw-bold fs-1x fs-lg-2x mb-5">{t('Scientific Articles', lang)}</h1>
                    <p className="fs-4 text-gray-600 lh-lg">
                      {t('scientific_desc', lang)}
                    </p>
                  </div>
                </div>
              </div>
              <div className={`tab-pane fade ${activeTab === 'v-pills-fairs' ? 'show active' : ''}`}>
                <div className="row">
                  <div className="col-1">
                    <div className="vertical-progress">
                      <div className="progress-bar" style={{ height: '65%' }}></div>
                    </div>
                  </div>
                  <div className="col-5 d-none d-md-block">
                    <img src={mobileImg} className="w-100 mobile-img p-5" style={{ marginTop: '-12rem' }} alt="mobile" />
                  </div>
                  <div className="col-md-6 col-10">
                    <h1 className="text-base lh-base fw-bold fs-1x fs-lg-2x mb-5">{t('Agricultural Fairs', lang)}</h1>
                    <p className="fs-4 text-gray-600 lh-lg">
                      {t('agricultural_desc', lang)}
                    </p>
                  </div>
                </div>
              </div>
              <div className={`tab-pane fade ${activeTab === 'v-pills-settings' ? 'show active' : ''}`}>
                <div className="row">
                  <div className="col-1">
                    <div className="vertical-progress">
                      <div className="progress-bar" style={{ height: '65%' }}></div>
                    </div>
                  </div>
                  <div className="col-5 d-none d-md-block">
                    <img src={mobileImg} className="w-100 mobile-img p-5" style={{ marginTop: '-12rem' }} alt="mobile" />
                  </div>
                  <div className="col-md-6 col-10">
                    <h1 className="text-base lh-base fw-bold fs-1x fs-lg-2x mb-5">{t('Agricultural Activities', lang)}</h1>
                    <p className="fs-4 text-gray-600 lh-lg">
                      {t('agricultural2_desc', lang)}
                    </p>
                  </div>
                </div>
              </div>
              <div className={`tab-pane fade ${activeTab === 'v-pills-outdoor' ? 'show active' : ''}`}>
                <div className="row">
                  <div className="col-1">
                    <div className="vertical-progress">
                      <div className="progress-bar" style={{ height: '80%' }}></div>
                    </div>
                  </div>
                  <div className="col-5 d-none d-md-block">
                    <img src={mobileImg} className="w-100 mobile-img p-5" style={{ marginTop: '-12rem' }} alt="mobile" />
                  </div>
                  <div className="col-6 col-md-6 col-10">
                    <h1 className="text-base lh-base fw-bold fs-1x fs-lg-2x mb-5">{t('Outdoor Plants', lang)}</h1>
                    <p className="fs-4 text-gray-600 lh-lg">
                      {t('outdoor_desc', lang)}
                    </p>
                  </div>
                </div>
              </div>
              <div className={`tab-pane fade ${activeTab === 'v-pills-indoor' ? 'show active' : ''}`}>
                <div className="row">
                  <div className="col-1">
                    <div className="vertical-progress">
                      <div className="progress-bar" style={{ height: '100%' }}></div>
                    </div>
                  </div>
                  <div className="col-5 d-none d-md-block">
                    <img src={mobileImg} className="w-100 mobile-img p-5" style={{ marginTop: '-12rem' }} alt="mobile" />
                  </div>
                  <div className="col-6 col-md-6 col-10">
                    <h1 className="text-base lh-base fw-bold fs-1x fs-lg-2x mb-5">{t('Indoor Plants', lang)}</h1>
                    <p className="fs-4 text-gray-600 lh-lg">
                      {t('indoor_desc', lang)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="faqs mt-20" id="faqs">
        <div className="container" data-aos="flip-right">
          <h1 className="lh-base fw-bold fs-2x fs-lg-3x mb-5 text-center">{t('Questions and Answers', lang)}:</h1>
          <div className="accordion mt-15" id="accordionExample">
            <div className="accordion-item">
              <h2 className="accordion-header" id="headingOne">
                <button 
                  className={`accordion-button cAccordion ${openAccordion === 'collapseOne' ? '' : 'collapsed'}`} 
                  type="button" 
                  onClick={() => setOpenAccordion(openAccordion === 'collapseOne' ? null : 'collapseOne')}
                  aria-expanded={openAccordion === 'collapseOne'}
                  aria-controls="collapseOne"
                >
                  {t('question1', lang)}
                </button>
              </h2>
              <div 
                id="collapseOne" 
                className={`accordion-collapse collapse ${openAccordion === 'collapseOne' ? 'show' : ''}`} 
                aria-labelledby="headingOne" 
                data-bs-parent="#accordionExample"
              >
                <div className="accordion-body fs-6" dangerouslySetInnerHTML={{ __html: t('answer1', lang) }}></div>
              </div>
            </div>
            <div className="accordion-item">
              <h2 className="accordion-header" id="headingTwo">
                <button 
                  className={`accordion-button cAccordion ${openAccordion === 'collapseTwo' ? '' : 'collapsed'}`} 
                  type="button" 
                  onClick={() => setOpenAccordion(openAccordion === 'collapseTwo' ? null : 'collapseTwo')}
                  aria-expanded={openAccordion === 'collapseTwo'}
                  aria-controls="collapseTwo"
                >
                  {t('question2', lang)}
                </button>
              </h2>
              <div 
                id="collapseTwo" 
                className={`accordion-collapse collapse ${openAccordion === 'collapseTwo' ? 'show' : ''}`} 
                aria-labelledby="headingTwo" 
                data-bs-parent="#accordionExample"
              >
                <div className="accordion-body fs-6" dangerouslySetInnerHTML={{ __html: t('answer2', lang) }}></div>
              </div>
            </div>
            <div className="accordion-item">
              <h2 className="accordion-header" id="headingThree">
                <button 
                  className={`accordion-button cAccordion ${openAccordion === 'collapseThree' ? '' : 'collapsed'}`} 
                  type="button" 
                  onClick={() => setOpenAccordion(openAccordion === 'collapseThree' ? null : 'collapseThree')}
                  aria-expanded={openAccordion === 'collapseThree'}
                  aria-controls="collapseThree"
                >
                  {t('question3', lang)}
                </button>
              </h2>
              <div 
                id="collapseThree" 
                className={`accordion-collapse collapse ${openAccordion === 'collapseThree' ? 'show' : ''}`} 
                aria-labelledby="headingThree" 
                data-bs-parent="#accordionExample"
              >
                <div className="accordion-body fs-6" dangerouslySetInnerHTML={{ __html: t('answer3', lang) }}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="download mt-20" id="download" style={{ backgroundImage: `url(${bg})`, backgroundRepeat: 'no-repeat', backgroundSize: 'cover', backgroundPosition: 'top' }}>
        <div className="container" data-aos="flip-left">
          <div className="row">
            <div className="col-6 px-lg-20 text-center">
              <img src={mobileImg} className="w-75 mt-20 rounded" alt="mobile" />
            </div>
            <div className="col-6 my-auto">
              <h1 className="lh-base fw-bold fs-2x fs-lg-3x mb-5 text-white">{t('Download Our Mobile App', lang)}</h1>
              <br />
              <div className="d-lg-flex d-block">
                <a href="https://play.google.com/store/apps/details?id=com.fesilah.app" target="_blank" rel="noopener noreferrer">
                  <img src={google} className={`w-lg-250px w-100px ${lang === 'ar' ? 'ms-5' : 'me-5'} mb-5 image`} alt="download" />
                </a>
                <a href="https://apps.apple.com/us/app/fesilah/id6449580295" target="_blank" rel="noopener noreferrer">
                  <img src={apple} className="w-lg-250px w-100px image" alt="download" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="contact mt-20 p-lg-15 p-5" id="contact">
        <div className="container" data-aos="flip-right">
          <h1 className="lh-base fw-bold fs-1x fs-lg-1x mb-5 p-5 text-white">{t('Contact Us', lang)}</h1>
          <form 
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                const result = await submitContact(contactFormData).unwrap();
                setContactFormData({ name: '', email: '', message: '' });
                showSuccess(result?.message || 'Thank you for contacting us! We\'ll get back to you soon.');
              } catch (err: any) {
                showError(err?.data?.message || 'Failed to submit contact form. Please try again.');
              }
            }} 
            data-aos="flip-left"
          >
            <div className="row mx-auto g-lg-10 mb-10 mb-lg-20">
              <div className="col-lg-6">
                <input 
                  type="text" 
                  name="name" 
                  placeholder={`${t('Full Name', lang)}*`} 
                  required 
                  className="form-control custom-input mb-10 text-white" 
                  value={contactFormData.name}
                  onChange={(e) => setContactFormData({ ...contactFormData, name: e.target.value })}
                />
              </div>
              <div className="col-lg-6">
                <input 
                  type="email" 
                  name="email" 
                  placeholder={`${t('Email', lang)}*`} 
                  required 
                  className="form-control custom-input mb-10 text-white" 
                  value={contactFormData.email}
                  onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
                />
              </div>
              <div className="col-lg-12">
                <textarea 
                  name="message" 
                  className="form-control custom-input mb-10 text-white" 
                  placeholder={t('Your Message', lang)} 
                  maxLength={300} 
                  rows={3}
                  value={contactFormData.message}
                  onChange={(e) => setContactFormData({ ...contactFormData, message: e.target.value })}
                ></textarea>
              </div>
              <div className="col-lg-6">
                <button 
                  type="submit" 
                  className="btn px-15 fs-lg-4 btn-white rounded"
                  disabled={isSubmittingContact}
                >
                  {isSubmittingContact ? t('Sending...', lang) : t('Just Send', lang)}
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>

      <LandingFooter />
    </>
  );
};

export default Home;
